import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { pageHead } from "../empty";
import { archiveModal, field, pills, refuse, select } from "../clients/forms";
import { inboxChanged } from "./inbox";

// Inbox › Connected inboxes, Admin only (briefs 1B-3, 1B-6; mockup v21 inbox/sources): every
// mailbox, number and fax sender FlowCare reads. Each is checked with its provider every 15 minutes
// (and with Check all now) and says Working, Needs attention or Not working, with when it was
// checked and when the last message came in; the ones with a problem are named in the banner, and
// the Admins get a notification. Edit changes a connection in place: its name, what it goes to, the
// business and default for its line, and for a phone line whose phones ring, for how long and the
// greeting. Add and Remove change the allowed list; only what's listed here is ever read.

interface Connection {
  id: string;
  channel: "email" | "text" | "fax";
  address: string;
  name: string;
  kindLabel: string;
  provider: string;
  goesTo: string;
  today: number;
  keptOut: number;
  lastIn: string | null;
  check: { state: string; label: string; level: string; reason: string | null; at: string | null };
  /** The mailbox RingCentral accepts email-to-fax from (brief 1B-4). */
  sendsFaxes: boolean;
  line: {
    id: string;
    kind: "phone" | "email";
    label: string;
    businessId: string | null;
    defaultFor: string[];
    ringUsers: string[];
    ringNames: string[];
    ringSeconds: number;
    greeting: string | null;
  } | null;
}
interface View {
  inboxes: Connection[];
  attention: { id: string; name: string; reason: string }[];
  people: { id: string; name: string; phone: string | null }[];
  businesses: { id: string; name: string }[];
  modulesOn: string[];
  textWebhook: string;
  voiceWebhook: string;
  mailReady: boolean;
  textsReady: boolean;
}

const MODULE_NAME: Record<string, string> = {
  staffing: "Staffing",
  homecare: "Home Care",
  transport: "Transportation",
  jobs: "Job board",
};

export async function connectedScreen(host: HTMLElement, ctx: ScreenContext) {
  const body = el("div");
  const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add");
  host.append(pageHead(ctx, "Every mailbox, number and fax line FlowCare reads.", [add]), body);
  let view: View;

  async function load() {
    const res = await api<View>("GET", "/api/inbox/connected");
    if (!res.ok) {
      body.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Connected inboxes couldn't be loaded."),
      );
      return;
    }
    view = res.body;
    draw();
  }

  const setupNote = (title: string, text: string) =>
    el(
      "div",
      { class: "note warnnote" },
      el("p", {}, icon("alert"), " ", el("b", {}, title), text),
    );

  function draw() {
    const rows = view.inboxes;
    // One mailbox sends faxes: RingCentral's email-to-fax accepts mail only from it.
    const faxSender = (c: Connection) => {
      if (c.channel !== "email") return null;
      const box = el("input", { type: "checkbox", checked: c.sendsFaxes });
      box.addEventListener("change", async () => {
        const res = await api<View>("PUT", `/api/inbox/connected/${c.id}/fax-sender`, {
          on: box.checked,
        });
        if (!res.ok) return toast("Not saved", res.body.error);
        view = res.body;
        draw();
        toast(box.checked ? `Faxes go from ${c.address}` : "No mailbox sends faxes now");
      });
      return el(
        "label",
        { class: "small", style: "display:flex;gap:6px;align-items:center;font-weight:400" },
        box,
        " Sends faxes (RingCentral email-to-fax)",
      );
    };
    const remove = (c: Connection) =>
      ib("x", `Remove ${c.name}`, () =>
        archiveModal(c.name, `/api/inbox/connected/${c.id}/remove`, async () => {
          toast("Removed", "It isn't read any more. Its messages stay.");
          await load();
        }),
      );
    const checkAll = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("sync"),
      " Check all now",
    );
    checkAll.addEventListener("click", async () => {
      checkAll.disabled = true;
      const res = await api<View & { checked: number }>("POST", "/api/inbox/connected/check-all");
      checkAll.disabled = false;
      if (!res.ok) return toast("Not checked", res.body.error);
      view = res.body;
      draw();
      inboxChanged();
      toast(
        "Checked a moment ago",
        `${res.body.checked} connection${res.body.checked === 1 ? "" : "s"}`,
      );
    });
    const n = view.attention.length;
    body.replaceChildren(
      ...(n
        ? [
            el(
              "div",
              { class: "note warnnote", role: "status", "aria-label": "Needs attention" },
              el(
                "p",
                {},
                icon("alert"),
                " ",
                el("b", {}, `${n} connection${n > 1 ? "s need" : " needs"} attention.`),
                " ",
                view.attention.map((a) => `${a.name}: ${a.reason}`).join(" "),
              ),
            ),
          ]
        : []),
      ...(!view.mailReady
        ? [
            setupNote(
              "Google Workspace isn't set up yet.",
              " Mailboxes are listed but not read until GOOGLE_SA_JSON is set (docs/SETUP.md).",
            ),
          ]
        : []),
      ...(!view.textsReady
        ? [setupNote("Texts aren't set up yet.", " Add the Twilio settings (docs/SETUP.md).")]
        : []),
      el(
        "section",
        { class: "card" },
        el("div", { class: "cardhead" }, el("h2", {}, "Connections"), checkAll),
        rows.length
          ? table<Connection>(
              [
                {
                  h: "Inbox",
                  f: (c) =>
                    el(
                      "span",
                      {},
                      el("b", {}, c.name),
                      el(
                        "div",
                        { class: "small muted" },
                        `${c.kindLabel} · ${c.provider}${
                          c.line?.ringNames.length
                            ? ` · calls ring ${c.line.ringNames.join(", ")}`
                            : ""
                        }`,
                      ),
                      faxSender(c),
                    ),
                },
                { h: "Goes to", f: (c) => c.goesTo || "–" },
                { h: "Today", num: true, f: (c) => String(c.today) },
                { h: "Kept out", num: true, f: (c) => String(c.keptOut) },
                { h: "Last in", f: (c) => c.lastIn ?? "Nothing yet" },
                {
                  h: "Status",
                  f: (c) =>
                    el(
                      "span",
                      {},
                      el(
                        "span",
                        { "data-tip": c.check.reason ?? undefined },
                        chip(c.check.label, c.check.level),
                      ),
                      el(
                        "div",
                        { class: "small muted" },
                        c.check.at ? `checked ${c.check.at}` : "not checked yet",
                      ),
                    ),
                },
                {
                  h: "",
                  f: (c) => {
                    const edit = el(
                      "button",
                      { type: "button", class: "btn sm", "aria-label": `Edit ${c.name}` },
                      icon("pencil"),
                      " Edit",
                    );
                    edit.addEventListener("click", () => editConnection(c));
                    return el("span", { class: "rowacts" }, edit, remove(c));
                  },
                },
              ],
              rows,
              undefined,
              "Connections",
            )
          : el(
              "div",
              { class: "blank" },
              icon("plug", "big"),
              el("h2", {}, "Nothing connected yet"),
              el(
                "p",
                { class: "muted" },
                "Add the office mailboxes, the numbers and the fax sender.",
              ),
            ),
        el(
          "p",
          { class: "small muted" },
          `Twilio sends this company's texts to ${location.origin}${view.textWebhook} and its calls to ${location.origin}${view.voiceWebhook}`,
        ),
      ),
      el(
        "section",
        { class: "card" },
        el("h2", {}, icon("inbox"), " How the mail is sorted (AI is off)"),
        el(
          "ul",
          { class: "plist2" },
          el(
            "li",
            {},
            "Linked to the client or person whose email, mobile or fax number matches exactly one record.",
          ),
          el(
            "li",
            {},
            "Not matched and about a referral, an application or a job: ",
            el("b", {}, "New contacts"),
            ".",
          ),
          el(
            "li",
            {},
            "Everything else waits in ",
            el("b", {}, "Needs action"),
            " with Link to… for a person to sort.",
          ),
          el("li", {}, "A person answers each one, and the reply goes on the record."),
        ),
      ),
    );
  }

  const businessOptions = () => [
    ["", "Any business"] as const,
    ...view.businesses.map((b) => [b.id, b.name] as const),
  ];
  const moduleOptions = () =>
    ["staffing", "homecare", "transport", "jobs"]
      .filter((m) => view.modulesOn.includes(m))
      .map((m) => [m, MODULE_NAME[m] ?? m] as const);

  /** The mockup's Edit connection: in place, so its history stays whole. */
  function editConnection(c: Connection) {
    const line = c.line;
    const phone = line?.kind === "phone";
    const name = line ? field("Name", line.label, { maxlength: "60" }) : null;
    const goes = field("Goes to", c.goesTo, { maxlength: "120" });
    const biz = line ? select("Business", businessOptions(), line.businessId ?? "") : null;
    const def = line
      ? pills(
          "Default From for",
          "checkbox",
          moduleOptions(),
          line.defaultFor,
          "One default per module: picking it here takes it off the other line.",
        )
      : null;
    const ring =
      phone && line
        ? pills(
            "When someone calls, ring",
            "checkbox",
            view.people.map((p) => [p.id, p.phone ? p.name : `${p.name} (no phone set)`] as const),
            line.ringUsers,
            "Their own phones ring at the same time. The first to answer gets the call.",
          )
        : null;
    // Someone without their own phone can't be rung (Settings › Integrations › Calls).
    for (const input of ring?.inputs ?? [])
      if (!view.people.find((p) => p.id === input.value)?.phone && !input.checked)
        input.disabled = true;
    const ringFor =
      phone && line
        ? select(
            "Ring for",
            [
              ["15", "15 seconds"],
              ["25", "25 seconds"],
              ["40", "40 seconds"],
            ],
            String(line.ringSeconds),
          )
        : null;
    const greeting =
      phone && line
        ? field("Greeting before it rings (optional)", line.greeting ?? "", {
            maxlength: "200",
            placeholder: "Thanks for calling. Connecting you now.",
          })
        : null;
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
    const m = openModal({
      title: "Edit connection",
      sub: `${c.name} · ${c.provider}`,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          name?.el,
          goes.el,
          biz?.el,
          def?.el,
          ring?.el,
          ringFor?.el,
          greeting?.el,
          phone
            ? el(
                "p",
                { class: "small muted" },
                "Unanswered calls go to voicemail and land in the Inbox as a Missed call.",
              )
            : null,
        ),
      ],
      actions: [cancel, save],
    });
    cancel.addEventListener("click", () => m.close());
    (name?.input ?? goes.input).focus();
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api<View>("PUT", `/api/inbox/connected/${c.id}`, {
        goesTo: goes.input.value,
        ...(line && name && biz && def
          ? {
              label: name.input.value,
              businessId: biz.input.value || null,
              defaultFor: def.values(),
            }
          : {}),
        ...(ring && ringFor && greeting
          ? {
              ringUsers: ring.values(),
              ringSeconds: Number(ringFor.input.value),
              greeting: greeting.input.value,
            }
          : {}),
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      view = res.body;
      draw();
      toast("Saved", "Applies to the next message or call");
    });
  }

  add.addEventListener("click", () => {
    const kind = select(
      "What",
      [
        ["email", "Mailbox (Google Workspace)"],
        ["text", "Number for texts and calls (Twilio)"],
        ["fax", "Fax sender (RingCentral)"],
      ],
      "email",
    );
    const address = field("Email or number", "", { placeholder: "intake@yourcompany.example" });
    const goes = field("Goes to", "", { placeholder: "Home Care referrals", maxlength: "120" });
    const label = field("Line name", "", { placeholder: "Home Care line", maxlength: "60" });
    const biz = select("Business", businessOptions(), "");
    kind.input.addEventListener("change", () => {
      label.el.hidden = biz.el.hidden = kind.input.value === "fax";
      address.input.placeholder =
        kind.input.value === "text"
          ? "410-555-0123"
          : kind.input.value === "fax"
            ? "fax@ringcentral.example"
            : "intake@yourcompany.example";
    });
    const save = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add");
    const m = openModal({
      title: "Add to Connected inboxes",
      sub: "Only what's listed here is ever read.",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          kind.el,
          address.el,
          goes.el,
          label.el,
          biz.el,
        ),
      ],
      actions: [save],
    });
    address.input.focus();
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api<View>("POST", "/api/inbox/connected", {
        channel: kind.input.value,
        address: address.input.value,
        goesTo: goes.input.value,
        ...(kind.input.value === "fax"
          ? {}
          : { label: label.input.value, businessId: biz.input.value || null }),
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      view = res.body;
      draw();
      toast("Added", address.input.value);
    });
  });

  await load();
}
